import { MaasRuntimeType, MaasWorkloadType } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';

import type { DeploySceneConfig } from '../../platform/scenes/types';
import { APP_INSTANCE_MODULES } from './modules';

export const APP_INSTANCE_ALL_MODULES = APP_INSTANCE_MODULES.map((m) => m.key);

/** SKU 可见模块（含运行配置，与直建一致；envVars/service 独立勾选，对齐 CI） */
export const APP_INSTANCE_SKU_MODULE_KEYS = [
  'runtimeConfig',
  'appService',
  'envVars',
  'nodeGroups',
  'healthCheck',
  'service',
  'storage',
] as const;

export type AppInstanceSkuModuleKey = (typeof APP_INSTANCE_SKU_MODULE_KEYS)[number];

export const APP_INSTANCE_SKU_MODULE_LIST: string[] = [...APP_INSTANCE_SKU_MODULE_KEYS];

/** 下单可见模块：与 SKU 模块一致（含运行配置） */
export const APP_INSTANCE_ORDER_MODULE_KEYS = [...APP_INSTANCE_SKU_MODULE_KEYS] as const;

export type AppInstanceOrderModuleKey = (typeof APP_INSTANCE_ORDER_MODULE_KEYS)[number];

export const APP_INSTANCE_ORDER_MODULE_LIST: string[] = [...APP_INSTANCE_ORDER_MODULE_KEYS];

/** 直建合并「资源配置」步模块（含基本信息 / 运行配置） */
export const APP_INSTANCE_DIRECT_MERGED_MODULES = [
  'baseInfo',
  'runtimeConfig',
  'appService',
  'envVars',
  'nodeGroups',
  'healthCheck',
  'service',
  'storage',
] as const;

export type AppInstanceDirectMergedModuleKey = (typeof APP_INSTANCE_DIRECT_MERGED_MODULES)[number];

export interface AppInstanceDirectCreateStep {
  key: string;
  title: string;
  modules: string[];
}

/** 直建两步：资源配置 + 确认（确认步由页面追加） */
export const APP_INSTANCE_DIRECT_CREATE_STEPS: AppInstanceDirectCreateStep[] = [
  { key: 'resource', title: '资源配置', modules: [...APP_INSTANCE_DIRECT_MERGED_MODULES] },
];

export function filterAppInstanceDirectMergedModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const byKey = new Map(modules.map((mod) => [mod.key, mod]));
  return APP_INSTANCE_DIRECT_MERGED_MODULES.map((key) => byKey.get(key)).filter((mod): mod is T => !!mod);
}

/** 下单模块标题 i18n key（L3 用 t(key, mod.title) 回退） */
export const APP_INSTANCE_ORDER_MODULE_TITLE: Record<string, string> = {
  baseInfo: 'data-center:maas.step.basic',
  runtimeConfig: 'data-center:maas.runtime.config',
  appService: 'data-center:maas.app.image',
  envVars: 'data-center:maas.common.env.vars',
  service: 'data-center:maas.section.service',
  healthCheck: 'data-center:probe',
  nodeGroups: 'data-center:maas.step.groups',
  storage: 'data-center:maas.section.storage',
};

/** 下单 deliveryMetadata 在表单缺 runtime 时的回退值 */
export const APP_INSTANCE_ORDER_DELIVERY_RUNTIME = {
  type: MaasRuntimeType.ContainerInstance,
  workload: MaasWorkloadType.ContainerInstance,
} as const;

const APP_INSTANCE_SKU_MODULE_KEY_SET = new Set<string>(APP_INSTANCE_SKU_MODULE_KEYS);

export function filterAppInstanceSkuModules<T extends { key: string }>(modules: readonly T[]): T[] {
  return modules.filter((mod) => APP_INSTANCE_SKU_MODULE_KEY_SET.has(mod.key));
}

export const APP_INSTANCE_SCENES: Record<'direct' | 'sku' | 'order', DeploySceneConfig> = {
  direct: {
    modules: [...APP_INSTANCE_ALL_MODULES],
    linkage: false,
  },
  sku: {
    modules: [...APP_INSTANCE_SKU_MODULE_KEYS],
    linkage: false,
  },
  order: {
    modules: [...APP_INSTANCE_ORDER_MODULE_KEYS],
    linkage: false,
  },
};

export function resolveAppInstanceSceneConfig(scene?: 'direct' | 'sku' | 'order'): DeploySceneConfig {
  return APP_INSTANCE_SCENES[scene ?? 'direct'];
}
